import { useState } from "react";
import { FiFilter, FiDownload, FiRefreshCw } from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import { hasPermission } from "../../utils/permissions";
import ReportFilterModal from "../../components/reports/ReportFilterModal";
import SearchableSelect from "../../components/reports/SearchableSelect";
import useReportSort from "../../components/reports/useReportSort";
const titles = {
  "catalogue-exceptions": "Barcode & Catalogue Exceptions",
  "customer-retention": "Customer Retention",
  "coupon-performance": "Promotion & Coupon Performance",
  "slow-moving": "Slow-moving Stock",
  "transfer-reconciliation": "Stock Transfer Reconciliation",
  "advance-outstanding": "Advance Recovery & Outstanding",
  "attendance-exceptions": "HRMS Missed Punch & Session Exceptions",
  "online-fulfilment": "Online Cancellation & Fulfilment",
  "price-grn-audit": "Price Change & GRN Edit Audit",
  "grn-reconciliation": "GRN Invoice Reconciliation",
  "counter-shift": "Counter & Shift Reconciliation",
  "brand-trading": "Brand-wise Trading Performance",
  "dead-stock": "Dead Stock & Markdown Candidates",
  "size-sell-through": "Size-wise Sell-through & Broken Sizes",
  "reorder-stock-cover": "Reorder & Stock Cover",
  "customer-loyalty-ledger": "Customer Loyalty Ledger",
  "gift-card-register": "Gift Card Register",
  "stock-adjustment-variance": "Stock Adjustment & Count Variance",
  "season-wise-stock": "Season-wise Stock",
  "channel-wise-sales": "Channel-wise Sales",
  "customer-purchase-history": "Customer Purchase History",
  "product-profitability": "Product Profitability",
  "inventory-movement": "Inventory Movement Register",
  "supplier-purchase-return": "Supplier Purchase & Return Summary",
  "purchase-returns": "Purchase Return Register",
  "sales-returns": "Sales Return Register",
  "online-payments": "Online Payment & Refund Register",
  "online-product-readiness": "Online Product Readiness",
  "stock-ageing": "Stock Ageing Report",
  "discount-loyalty": "Discount & Loyalty Redemption Report",
  "online-orders-detailed": "Online Order Detailed Report",
};
const label = (k) =>
  k.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^./, (c) => c.toUpperCase());
const today = () => new Date(Date.now() + 19800000).toISOString().slice(0, 10);
const cleanFilters = (filters) =>
  Object.fromEntries(
    Object.entries(filters).filter(([, v]) => v !== "" && v != null),
  );
const defaults = (kind) => ({
  from: kind === "reorder-stock-cover" ? new Date(Date.parse(today())-30*86400000).toISOString().slice(0,10) : kind === "stock-ageing" ? "" : today().slice(0, 7) + "-01",
  to: kind === "reorder-stock-cover" ? new Date(Date.parse(today())-86400000).toISOString().slice(0,10) : today(),
  search: "",
  store: "",
  brand: "",
  category: "",
  season: "",
  status: "",
  channel: "",
  ...(kind === "reorder-stock-cover" ? {leadTimeDays:7,safetyDays:7,targetCoverDays:30} : {}),
  ...(kind === "dead-stock" ? {from:"",to:"",minimumAgeDays:90,noSaleDays:90,status:"Markdown candidate"} : {}),
  ...(kind === "transfer-reconciliation" ? {dateBasis:"createdAt"} : {}),
  ...(kind === "catalogue-exceptions" ? {from:"",to:""} : {}),
  ...(kind === "customer-retention" ? {atRiskDays:90,dormantDays:180} : {}),
  ...(kind === "slow-moving" ? {from:new Date(Date.parse(today())-30*86400000).toISOString().slice(0,10),to:new Date(Date.parse(today())-86400000).toISOString().slice(0,10),maximumNetSold:5,minimumCoverDays:60,status:"Slow-moving"} : {}),
});
export default function OperationalReportPage({ kind }) {
  const { accessToken, user } = useAuth(),
    permission = "reports." + kind.replaceAll("-", "_");
  const [raw, setRaw] = useState(null),
    [filterOptions, setFilterOptions] = useState(null),
    [optionsLoading, setOptionsLoading] = useState(false),
    [optionsError, setOptionsError] = useState(""),
    [draft, setDraft] = useState(() => defaults(kind)),
    [applied, setApplied] = useState(() => defaults(kind)),
    [open, setOpen] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [page, setPage] = useState(1),
    [size, setSize] = useState(100);
  const { report, controls } = useReportSort(raw, () => setPage(1));
  const rows = report?.rows || [],
    columns = raw?.columns || [],
    pages = Math.max(1, Math.ceil(rows.length / size));
  const generate = async (filters) => {
    setBusy(true);
    setError("");
    try {
      const data = await apiRequest(
        "/operational-reports/" + kind + "/generate",
        { method: "POST", body: cleanFilters(filters), accessToken },
      );
      setRaw(data);
      setApplied({ ...filters });
      setPage(1);
      setOpen(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const exportFile = async () => {
    setBusy(true);
    setError("");
    try {
      const response = await apiRequest(
        "/operational-reports/" + kind + "/export",
        {
          method: "POST",
          body: cleanFilters(applied),
          accessToken,
          rawResponse: true,
        },
      );
      const url = URL.createObjectURL(await response.blob()),
        a = document.createElement("a");
      a.href = url;
      a.download = kind + ".xlsx";
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  if (!hasPermission(user, permission + ".view"))
    return (
      <p className="p-6">You do not have permission to view this report.</p>
    );
  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-primary-800">
            Business reports
          </p>
          <h1 className="mt-1 text-xl font-bold text-slate-950">
            {titles[kind]}
          </h1>
        </div>
        <div className="flex gap-2">
          <button
            className="flex items-center gap-2 rounded-lg border px-4 py-2"
            onClick={async () => {
              setDraft({ ...applied });
              setOpen(true);
              if (["catalogue-exceptions","customer-retention","coupon-performance","slow-moving","transfer-reconciliation","advance-outstanding","attendance-exceptions","online-fulfilment","price-grn-audit","grn-reconciliation","counter-shift","brand-trading","dead-stock","reorder-stock-cover","size-sell-through"].includes(kind)) {
                setOptionsLoading(true);
                setOptionsError("");
                try { setFilterOptions(await apiRequest("/operational-reports/"+kind+"/options",{accessToken})); }
                catch(e) { setOptionsError("Could not load filter options: "+e.message); }
                finally { setOptionsLoading(false); }
              }
            }}
          >
            <FiFilter />
            Filters
          </button>
          <button
            disabled={busy || !hasPermission(user, permission + ".generate")}
            className="flex items-center gap-2 rounded-lg bg-primary-800 px-4 py-2 text-white disabled:opacity-40"
            onClick={() => generate(applied)}
          >
            <FiRefreshCw />
            {busy ? "Working…" : "Generate"}
          </button>
          <button
            disabled={
              busy || !raw || !hasPermission(user, permission + ".export")
            }
            className="flex items-center gap-2 rounded-lg border px-4 py-2 disabled:opacity-40"
            onClick={exportFile}
          >
            <FiDownload />
            Export Excel
          </button>
        </div>
      </header>
      {kind === "stock-ageing" && (
        <p className="text-sm text-slate-500">
          Current stock snapshot. Age is measured from the lot receipt date;
          repeat receipts into the same lot share that date.
        </p>
      )}
      {kind === "inventory-movement" && (
        <p className="text-sm text-slate-500">
          Opening and running closing quantities are per variant and store.
          Movement filters hide rows without changing the running balances.
        </p>
      )}
      {kind === "purchase-returns" && (
        <p className="text-sm text-slate-500">
          Dispatched and closed supplier returns only. Product return values
          exclude freight; GST is prorated from the original GRN.
        </p>
      )}
      {kind === "online-payments" && (
        <p className="text-sm text-slate-500">
          One row per payment attempt, plus orders without attempts. Date
          filters use attempt initiation/order placement dates. Order amounts
          repeat across attempts: do not sum them. Refund statuses are recorded;
          refund amounts/dates are unavailable and shown as blank.
        </p>
      )}
      {kind === "online-product-readiness" && (
        <p className="text-sm text-slate-500">
          Current product/variant snapshot using the storefront lot-pricing
          rules. Flags include hidden products, missing prices/content and
          unavailable online stock. This report does not change products.
        </p>
      )}
      {kind === "customer-loyalty-ledger" && (
        <p className="text-sm text-slate-500">
          Balances are the stored balances for each transaction, not totals
          across customers. Opening = recorded closing balance minus the signed
          points change.
        </p>
      )}
      {kind === "stock-adjustment-variance" && (
        <p className="text-sm text-slate-500">
          System-before and counted-after quantities are reconstructed from the
          complete recorded movement ledger. A zero-variance count is absent if
          no movement was recorded.
        </p>
      )}
      {kind === "season-wise-stock" && (
        <p className="text-sm text-slate-500">
          Lot-level stock by season. Opening/closing quantities are
          reconstructed from lot movements; transfers and rollover appear as
          other movements, not purchases or sales.
        </p>
      )}
      {[
        "channel-wise-sales",
        "customer-purchase-history",
        "product-profitability",
      ].includes(kind) && (
        <p className="text-sm text-slate-500">
          Completed POS sales, issued invoices and paid active online orders.
          Orders and challans are not counted as extra ERP sales. POS/invoice
          returns are negative; online refund event amounts/dates are not stored
          and are not estimated.
        </p>
      )}
      {kind === "product-profitability" && (
        <p className="text-sm text-slate-500">
          Gross margin uses sales excluding recorded GST and verified lot-cost
          snapshots. Missing/partial cost leaves profit blank. Shipping and
          operating expenses are excluded.
        </p>
      )}

      {kind === "sales-returns" && (
        <p className="text-sm text-slate-500">
          Completed POS returns and received sales-invoice returns. Values are
          positive return amounts; sales credits are not cash refunds.
        </p>
      )}
      {kind === "supplier-purchase-return" && (
        <p className="text-sm text-slate-500">
          Supplier/store summary of posted GRNs and dispatched or closed returns
          within the selected receipt/return dates. Freight stays separate. Net
          value is not an outstanding payment balance.
        </p>
      )}
      {kind === "discount-loyalty" && (
        <p className="text-sm text-slate-500">
          POS sale bills and online orders. Returns are not deducted here.
          Loyalty and coupon amounts are separate; discrepancies are flagged for
          review.
        </p>
      )}
      {error && (
        <p role="alert" className="rounded-lg bg-red-50 p-3 text-red-700">
          {error}
        </p>
      )}
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        {controls}
        <div className="flex items-center justify-between border-b p-3 text-sm">
          <span>
            {raw
              ? rows.length +
                " rows · Generated " +
                new Date(raw.generatedAt).toLocaleString()
              : "Generate the report to preview data"}
          </span>
          <label>
            Rows per page{" "}
            <select
              value={size}
              onChange={(e) => {
                setSize(Number(e.target.value));
                setPage(1);
              }}
              className="rounded border p-1"
            >
              {[100, 200, 300, 500, 1000].map((n) => (
                <option key={n}>{n}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="max-h-[65vh] overflow-auto">
          <table className="w-full border-collapse text-sm">
            <thead className="sticky top-0 z-10 bg-primary-800 text-white">
              <tr>
                <th className="whitespace-nowrap border border-primary-900 p-3">
                  S.No.
                </th>
                {columns.map((k) => (
                  <th
                    key={k}
                    className="whitespace-nowrap border border-primary-900 p-3 text-left"
                  >
                    {label(k)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.slice((page - 1) * size, page * size).map((r, i) => (
                <tr key={i} className="even:bg-slate-50 hover:bg-primary-50">
                  <td className="border border-slate-200 p-3">
                    {(page - 1) * size + i + 1}
                  </td>
                  {columns.map((k) => (
                    <td
                      key={k}
                      className={
                        "whitespace-nowrap border border-slate-200 p-3 " +
                        (kind === "counter-shift" && ["cashVariance","expectedCashMismatch"].includes(k) && typeof r[k] === "number" && r[k] !== 0 ? "bg-red-50 text-red-700 font-semibold " : "") +
                        (typeof r[k] === "number"
                          ? "text-right tabular-nums"
                          : "")
                      }
                    >
                      {typeof r[k] === "number"
                        ? r[k].toLocaleString("en-IN", {
                            maximumFractionDigits: 2,
                          })
                        : r[k] || "—"}
                    </td>
                  ))}
                </tr>
              ))}
              {!rows.length && (
                <tr>
                  <td
                    colSpan={columns.length + 1}
                    className="p-10 text-center text-slate-500"
                  >
                    {raw ? "No matching records." : "No report generated yet."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end gap-3 border-t p-3">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="rounded border px-3 py-1 disabled:opacity-40"
          >
            Previous
          </button>
          <span>
            Page {page} / {pages}
          </span>
          <button
            disabled={page >= pages}
            onClick={() => setPage((p) => p + 1)}
            className="rounded border px-3 py-1 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </section>
      <ReportFilterModal
        open={open}
        title={titles[kind] + " filters"}
        loading={busy}
        onClose={() => setOpen(false)}
        onApply={() =>
          hasPermission(user, permission + ".generate") && generate(draft)
        }
        applyLabel="Search"
      >
        <div className="grid gap-4 sm:grid-cols-2">
          {kind === "customer-retention" && <>{error && <p role="alert" className="sm:col-span-2 text-red-700">{error}</p>}{[['atRiskDays','At-risk after (days)',1,3650],['dormantDays','Dormant after (days)',2,7300]].map(([key,title,min,max])=><label key={key}>{title}<input type="number" min={min} max={max} step="1" value={draft[key]} onChange={e=>setDraft(current=>({...current,[key]:e.target.value}))} className="mt-1 block w-full rounded-lg border p-2"/></label>)}</>}
          {kind === "slow-moving" && <>{error && <p role="alert" className="sm:col-span-2 text-red-700">{error}</p>}{[['maximumNetSold','Maximum net units sold',0.001,1000000,0.001],['minimumCoverDays','Minimum stock cover (days)',1,3650,1]].map(([key,title,min,max,step])=><label key={key}>{title}<input type="number" min={min} max={max} step={step} value={draft[key]} onChange={e=>setDraft(current=>({...current,[key]:e.target.value}))} className="mt-1 block w-full rounded-lg border p-2"/></label>)}</>}
          {optionsLoading && <p role="status">Loading filter options…</p>}
          {optionsError && <p role="alert" className="text-red-700 sm:col-span-2">{optionsError}</p>}
          {kind === "reorder-stock-cover" && error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700 sm:col-span-2">{error}</p>}
          {kind === "reorder-stock-cover" && [['leadTimeDays','Lead time (days)',0,365],['safetyDays','Safety cover (days)',0,365],['targetCoverDays','Target stock cover (days)',1,730]].map(([key,title,min,max]) => <label key={key}>{title}<input type="number" min={min} max={max} step="1" value={draft[key]} onChange={e => setDraft(current => ({...current,[key]:e.target.value}))} className="mt-1 block w-full rounded-lg border p-2"/></label>)}
          {kind === "dead-stock" && <>{error && <p role="alert">{error}</p>}{[['minimumAgeDays','Minimum lot age (days)'],['noSaleDays','Minimum days without sale']].map(([key,title])=><label key={key}>{title}<input type="number" min="1" max="3650" step="1" value={draft[key]} onChange={e=>setDraft(current=>({...current,[key]:e.target.value}))} className="mt-1 block w-full rounded-lg border p-2"/></label>)}</>}
          {!["catalogue-exceptions", "dead-stock", "stock-ageing", "online-product-readiness"].includes(kind) &&
            ["from", "to"].map((k) => (
              <label key={k}>
                {kind === "counter-shift" ? label(k) + " (shift opened)" : kind === "online-fulfilment" ? label(k) + " (order placed)" : label(k)}
                <input
                  type="date"
                  value={draft[k]}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, [k]: e.target.value }))
                  }
                  className="mt-1 block w-full rounded-lg border p-2"
                />
              </label>
            ))}
          <label className="sm:col-span-2">
            {kind === "online-fulfilment" ? "Search order, customer, store or cancellation reason" : kind === "price-grn-audit" ? "Search GRN, lot, article, SKU or reason" : kind === "grn-reconciliation" ? "Search GRN, supplier, invoice or PO" : kind === "counter-shift" ? "Search counter, cashier or session" : kind === "brand-trading" ? "Search brand" : "Search product, SKU, lot or bill"}
            <input
              value={draft.search}
              onChange={(e) =>
                setDraft((d) => ({ ...d, search: e.target.value }))
              }
              className="mt-1 block w-full rounded-lg border p-2"
            />
          </label>
          {(kind === "catalogue-exceptions"
            ? ["brand","category","status","visibility","exception"]
            : kind === "customer-retention"
            ? ["customer","channel","status"]
            : kind === "coupon-performance"
            ? ["type","store","channel","status"]
            : kind === "slow-moving"
            ? ["store","brand","category","subCategory","sizeGroup","colour","size","status"]
            : kind === "transfer-reconciliation"
            ? ["dateBasis","sourceStore","destinationStore","brand","category","status","reconciliation"]
            : kind === "advance-outstanding"
            ? ["employee","branch","department","status","balanceStatus"]
            : kind === "attendance-exceptions"
            ? ["employee","branch","department","status","exception"]
            : kind === "online-fulfilment"
            ? ["store","status","paymentStatus","fulfilment"]
            : kind === "price-grn-audit"
            ? ["type","actor"]
            : kind === "grn-reconciliation"
            ? ["store","supplier","season","status","reconciliation"]
            : kind === "counter-shift"
            ? ["store","counter","cashier","status","reconciliation"]
            : kind === "brand-trading"
            ? ["store","brand","category"]
            : kind === "reorder-stock-cover"
            ? ["store","brand","category","subCategory","sizeGroup","colour","size","status"]
            : ["size-sell-through","dead-stock"].includes(kind)
            ? ["store", "brand", "category", "subCategory", "season", "sizeGroup", "colour", "size", "status"]
            : kind === "customer-loyalty-ledger"
            ? ["customer", "type"]
            : kind === "gift-card-register"
              ? ["status"]
              : kind === "stock-adjustment-variance"
                ? ["store", "brand", "category", "movementType"]
                : kind === "season-wise-stock"
                  ? ["store", "season", "brand", "category"]
                  : kind === "channel-wise-sales"
                    ? ["channel"]
                    : kind === "customer-purchase-history"
                      ? [
                          "customer",
                          "store",
                          "channel",
                          "brand",
                          "category",
                          "transaction",
                        ]
                      : kind === "product-profitability"
                        ? [
                            "store",
                            "channel",
                            "brand",
                            "category",
                            "costStatus",
                            "transaction",
                          ]
                        : kind === "online-payments"
                          ? [
                              "gateway",
                              "attemptStatus",
                              "paymentStatus",
                              "refundStatus",
                            ]
                          : kind === "online-product-readiness"
                            ? [
                                "brand",
                                "category",
                                "subCategory",
                                "visibility",
                                "readiness",
                              ]
                            : kind === "purchase-returns"
                              ? [
                                  "store",
                                  "supplier",
                                  "brand",
                                  "category",
                                  "status",
                                ]
                              : kind === "sales-returns"
                                ? [
                                    "store",
                                    "brand",
                                    "category",
                                    "channel",
                                    "status",
                                  ]
                                : kind === "inventory-movement"
                                  ? [
                                      "store",
                                      "brand",
                                      "category",
                                      "season",
                                      "movementType",
                                    ]
                                  : kind === "supplier-purchase-return"
                                    ? ["store", "supplier"]
                                    : kind === "stock-ageing"
                                      ? [
                                          "store",
                                          "brand",
                                          "category",
                                          "season",
                                          "ageBand",
                                        ]
                                      : kind === "discount-loyalty"
                                        ? ["store", "channel", "status"]
                                        : [
                                            "brand",
                                            "category",
                                            "status",
                                            "paymentStatus",
                                          ]
          ).map((k) => (
            <label key={k}>
              {kind === "coupon-performance" && k === "type" ? "Coupon" : label(k)}
              <SearchableSelect
                options={filterOptions?.[k] || raw?.options?.[k] || []}
                value={draft[k] || ""}
                onChange={(v) => setDraft((d) => ({ ...d, [k]: v }))}
                placeholder={"All " + (kind === "coupon-performance" && k === "type" ? "Coupon" : label(k))}
              />
            </label>
          ))}
          <button
            className="text-left text-sm text-primary-800"
            onClick={() => setDraft(defaults(kind))}
          >
            Reset filters
          </button>
        </div>
      </ReportFilterModal>
    </div>
  );
}
